import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateFinanceData } from '../../utils/financeQueries';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/ReceiptOutlined';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { useAuth } from '../../context/authContext';
import {
  approvePayment,
  downloadInvoice,
  downloadReceipt,
  getFinanceDashboard,
  getInvoiceBlob,
  getReceiptBlob,
  listPayments,
  rejectPayment,
} from '../../api/payments';
import { Payment, PAYMENT_STATUS_LABEL, PaymentStatus, PaymentType } from '../../types/payment';
import { ApprovalStatusChip } from '../finance/ApprovalStatusChip';
import { TableToolbar } from '../../components/table/TableToolbar';
import { PageHeader } from '../../components/PageHeader';
import { DocumentPreviewDialog, DocumentPreviewTarget } from '../../components/DocumentPreviewDialog';

const TYPE_COLOR: Record<PaymentType, 'primary' | 'info' | 'success' | 'warning'> = {
  PAYMENT: 'primary',
  DISCOUNT: 'info',
  SCHOLARSHIP: 'success',
  REFUND: 'warning',
};

const CollectionTile = ({ label, entries }: { label: string; entries: Array<{ currency: { symbol: string | null; code: string }; total: number }> | undefined }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    {!entries || entries.length === 0 ? (
      <Typography variant="h5">—</Typography>
    ) : (
      entries.map((e, i) => (
        <Typography key={i} variant="h5">
          {e.currency.symbol ?? e.currency.code} {e.total.toFixed(2)}
        </Typography>
      ))
    )}
  </Paper>
);

export const PaymentsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission, user } = useAuth();
  const [rejectTarget, setRejectTarget] = useState<Payment | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });
  const [status, setStatus] = useState<PaymentStatus | ''>('');
  const [type, setType] = useState<PaymentType | ''>('');
  const [previewTarget, setPreviewTarget] = useState<DocumentPreviewTarget | null>(null);

  const dashboardQuery = useQuery({ queryKey: ['finance-dashboard'], queryFn: getFinanceDashboard });

  const paymentsQuery = useQuery({
    queryKey: ['payments', paginationModel.page, paginationModel.pageSize, status, type],
    queryFn: () =>
      listPayments({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        status: status || undefined,
        type: type || undefined,
      }),
    placeholderData: (prev) => prev,
  });

  const invalidate = () => invalidateFinanceData(queryClient);
  const approveMutation = useMutation({ mutationFn: (id: number) => approvePayment(id), onSuccess: invalidate });
  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => rejectPayment(id, reason),
    onSuccess: () => {
      invalidate();
      setRejectTarget(null);
      setRejectReason('');
    },
  });
  const actionError = approveMutation.error ?? rejectMutation.error;

  const canApprove = hasPermission('payments', 'approve');
  // Maker-checker (Phase 97): nobody but Super Admin reviews their own
  // submission — the server refuses it; this just hides the buttons.
  const canReview = (p: Payment) =>
    canApprove && (p.recordedBy.id !== user?.id || user?.role.name === 'Super Admin');

  const columns: GridColDef<Payment>[] = useMemo(
    () => [
      { field: 'paymentNumber', headerName: 'Payment #', width: 130 },
      {
        field: 'student',
        headerName: 'Student',
        flex: 1,
        valueGetter: (_v, row) => `${row.student.firstName} ${row.student.lastName} (${row.student.studentNumber})`,
      },
      { field: 'category', headerName: 'Category', width: 140, valueGetter: (_v, row) => row.category.name },
      {
        field: 'type',
        headerName: 'Type',
        width: 120,
        renderCell: (params) => <Chip size="small" label={params.value} color={TYPE_COLOR[params.value as PaymentType]} />,
      },
      {
        field: 'amount',
        headerName: 'Amount',
        width: 130,
        valueGetter: (_v, row) => `${row.currency.symbol ?? row.currency.code} ${Number(row.amount).toFixed(2)}`,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 190,
        renderCell: (params) => <ApprovalStatusChip status={params.value as PaymentStatus} />,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 260,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<DescriptionIcon />}
              onClick={() =>
                setPreviewTarget({
                  key: `invoice-${params.row.id}`,
                  originalName: `Invoice ${params.row.paymentNumber}.pdf`,
                  mimeType: 'application/pdf',
                  fetchBlob: () => getInvoiceBlob(params.row.id),
                  onDownload: () => downloadInvoice(params.row.id, params.row.paymentNumber),
                })
              }
            >
              Invoice
            </Button>
            <Tooltip title="Download invoice">
              <IconButton size="small" onClick={() => downloadInvoice(params.row.id, params.row.paymentNumber)}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {params.row.receipt && (
              <>
                <Button
                  size="small"
                  startIcon={<ReceiptIcon />}
                  onClick={() =>
                    setPreviewTarget({
                      key: `receipt-${params.row.id}`,
                      originalName: `Receipt ${params.row.receipt!.receiptNumber}.pdf`,
                      mimeType: 'application/pdf',
                      fetchBlob: () => getReceiptBlob(params.row.id),
                      onDownload: () => downloadReceipt(params.row.id, params.row.receipt!.receiptNumber),
                    })
                  }
                >
                  Receipt
                </Button>
                <Tooltip title="Download receipt">
                  <IconButton
                    size="small"
                    onClick={() => downloadReceipt(params.row.id, params.row.receipt!.receiptNumber)}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {params.row.status === 'PENDING' && canReview(params.row) && (
              <>
                <Button
                  size="small"
                  color="success"
                  disabled={approveMutation.isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    approveMutation.mutate(params.row.id);
                  }}
                >
                  Approve
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    rejectMutation.reset();
                    setRejectTarget(params.row);
                  }}
                >
                  Reject
                </Button>
              </>
            )}
          </Stack>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- canReview derives from canApprove + user
    [canApprove, user, approveMutation, rejectMutation],
  );

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={PaymentsOutlinedIcon}
        color="#12B76A"
        title="Payments"
        subtitle="Review, approve and reject student payments."
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <CollectionTile label="Today's collection" entries={dashboardQuery.data?.todayCollection} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <CollectionTile label="This month's collection" entries={dashboardQuery.data?.monthCollection} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Collection by counselor
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {dashboardQuery.data?.collectionByCounselor.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No collections recorded yet.
                </Typography>
              )}
              {dashboardQuery.data?.collectionByCounselor.map((c, i) => (
                <Chip
                  key={i}
                  label={`${c.counselor.firstName} ${c.counselor.lastName}: ${c.currency.symbol ?? c.currency.code} ${c.total.toFixed(2)}`}
                  variant="outlined"
                />
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Status"
          sx={{ width: 200 }}
          value={status}
          onChange={(e) => setStatus(e.target.value as PaymentStatus | '')}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          <MenuItem value="PENDING">{PAYMENT_STATUS_LABEL.PENDING}</MenuItem>
          <MenuItem value="APPROVED">{PAYMENT_STATUS_LABEL.APPROVED}</MenuItem>
          <MenuItem value="REJECTED">{PAYMENT_STATUS_LABEL.REJECTED}</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Type"
          sx={{ width: 200 }}
          value={type}
          onChange={(e) => setType(e.target.value as PaymentType | '')}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          <MenuItem value="PAYMENT">Payment</MenuItem>
          <MenuItem value="DISCOUNT">Discount</MenuItem>
          <MenuItem value="SCHOLARSHIP">Scholarship</MenuItem>
          <MenuItem value="REFUND">Refund</MenuItem>
        </TextField>
      </Stack>

      {approveMutation.isError && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => approveMutation.reset()}>
          {isAxiosError(actionError)
            ? (actionError.response?.data?.message ?? "Couldn't approve the payment")
            : "Couldn't approve the payment"}
        </Alert>
      )}

      <Box sx={{ height: 600, bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid
          rows={paymentsQuery.data?.data ?? []}
          columns={columns}
          rowCount={paymentsQuery.data?.meta.total ?? 0}
          loading={paymentsQuery.isFetching}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
          slots={{ toolbar: TableToolbar }}
          disableRowSelectionOnClick
          onRowClick={(params) => navigate(`/students/${params.row.student.id}`)}
        />
      </Box>

      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject {rejectTarget?.paymentNumber}</DialogTitle>
        <DialogContent>
          <TextField
            label="Rejection reason"
            helperText="Required — the person who recorded it sees this."
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 1 }}
            inputProps={{ maxLength: 500 }}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
          {rejectMutation.isError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {isAxiosError(rejectMutation.error)
                ? (rejectMutation.error.response?.data?.message ?? "Couldn't reject the payment")
                : "Couldn't reject the payment"}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!rejectReason.trim() || rejectMutation.isPending}
            onClick={() =>
              rejectTarget && rejectMutation.mutate({ id: rejectTarget.id, reason: rejectReason.trim() })
            }
          >
            {rejectMutation.isPending ? 'Rejecting…' : 'Reject payment'}
          </Button>
        </DialogActions>
      </Dialog>

      <DocumentPreviewDialog target={previewTarget} onClose={() => setPreviewTarget(null)} />
    </Box>
  );
};
